import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, User, ArrowLeft, AlertCircle, Loader, Eye, EyeOff, FileText, CheckSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getUserRole, getRedirectPathByRole } from '../lib/auth';
import BackToHomeButton from '../components/BackToHomeButton';
import { trackUserAction } from '../lib/user-tracking';

interface LocationState {
  returnTo?: string;
  message?: string;
  isResetPassword?: boolean;
}

export default function Auth() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  useEffect(() => {
    // Verificar se o usuário já está logado
    const checkAuthStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Registrar login bem-sucedido
        await trackUserAction('auth_success', {
          method: 'auto_login',
          email: session.user.email
        });
        
        // Obter o caminho de redirecionamento com base no papel do usuário
        const redirectPath = await getRedirectPathByRole();
        navigate(state?.returnTo || redirectPath);
      }
    };

    checkAuthStatus();

    // Verificar se deve mostrar a tela de recuperação de senha
    if (state?.isResetPassword) {
      setIsResetPassword(true);
    }
  }, [navigate, state]);

  const validateForm = () => {
    setError('');
    
    if (isResetPassword) {
      if (!email) return 'Por favor, informe seu email';
      return '';
    }
    
    if (!email) return 'Por favor, informe seu email';
    if (!password) return 'Por favor, informe sua senha';
    
    if (isSignUp) {
      if (!name) return 'Por favor, informe seu nome';
      if (password.length < 6) return 'A senha deve ter pelo menos 6 caracteres';
      if (password !== confirmPassword) return 'As senhas não coincidem';
      if (!agreedToTerms) return 'Você precisa concordar com os Termos de Uso e Política de Privacidade';
    }
    
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isResetPassword) {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        
        if (resetError) throw resetError;
        
        // Registrar solicitação de redefinição de senha
        await trackUserAction('password_reset_request', {
          email
        });
        
        setSuccess('Enviamos um link para redefinir sua senha. Verifique seu email.');
      } else if (isSignUp) {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { 
              name,
              role: 'user', // Define o papel padrão como usuário
              agreedToTerms: true,
              agreedToTermsAt: new Date().toISOString()
            }
          }
        });
        
        if (signUpError) throw signUpError;

        if (data.user) {
          // Registrar cadastro bem-sucedido
          await trackUserAction('signup_success', {
            email: data.user.email
          });
          
          setSuccess('Cadastro realizado com sucesso! Você já pode fazer login.');
          setIsSignUp(false);
        }
      } else {
        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        
        if (signInError) throw signInError;

        if (data.user) {
          // Registrar login bem-sucedido
          await trackUserAction('login_success', {
            email: data.user.email
          });
          
          // Obter o papel do usuário e redirecionar para a página apropriada
          const redirectPath = await getRedirectPathByRole();
          navigate(state?.returnTo || redirectPath);
        }
      }
    } catch (error: any) {
      console.error('Erro de autenticação:', error);
      
      // Registrar falha de autenticação
      await trackUserAction('auth_error', {
        method: isSignUp ? 'signup' : (isResetPassword ? 'reset_password' : 'login'),
        email,
        error: error.message
      });
      
      // Tratamento de erros específicos
      if (error.message.includes('Email not confirmed')) {
        setError('Email não confirmado. Verifique sua caixa de entrada.');
      } else if (error.message.includes('Invalid login credentials')) {
        setError('Email ou senha incorretos.');
      } else if (error.message.includes('User already registered')) {
        setError('Este email já está cadastrado.');
      } else {
        setError(error.message || 'Ocorreu um erro. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsResetPassword(false);
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="p-4 flex justify-between items-center">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Voltar
        </button>
        <BackToHomeButton />
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-elevation-2 max-w-md w-full p-8">
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
            {isResetPassword ? 'Recuperar Senha' : isSignUp ? 'Criar Conta' : 'Entrar'}
          </h1>
          <p className="text-center text-gray-600 mb-8">
            {isResetPassword
              ? 'Informe seu email para receber um link de recuperação'
              : isSignUp
              ? 'Crie sua conta para participar da comunidade'
              : 'Entre para acessar sua conta'}
          </p>

          {state?.message && (
            <div className="bg-brand-light text-brand-teal p-4 rounded-2xl mb-6 text-sm">
              {state.message}
            </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-2xl mb-6 text-sm flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 text-green-600 p-4 rounded-2xl mb-6 text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isResetPassword && isSignUp && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-12 w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
                    placeholder="Seu nome completo"
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
                  placeholder="seu@email.com"
                  disabled={loading}
                />
              </div>
            </div>

            {!isResetPassword && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-12 w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            )}

            {isSignUp && (
              <>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar Senha
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-12 w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
                      placeholder="••••••••"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="terms"
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="h-4 w-4 text-brand-teal border-gray-300 rounded focus:ring-brand-teal"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="terms" className="text-gray-600">
                        Eu concordo com os{' '}
                        <button
                          type="button"
                          onClick={() => setTermsModalOpen(true)}
                          className="text-brand-teal hover:underline"
                        >
                          Termos de Uso
                        </button>{' '}
                        e{' '}
                        <button
                          type="button"
                          onClick={() => setPrivacyModalOpen(true)}
                          className="text-brand-teal hover:underline"
                        >
                          Política de Privacidade
                        </button>
                      </label>
                    </div>
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-teal text-white py-3 px-4 rounded-2xl hover:bg-brand-teal/90 transition-colors disabled:opacity-50 shadow-elevation-1 hover:shadow-elevation-2 active:shadow-elevation-1 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="h-5 w-5 mr-2 animate-spin" />
                  Processando...
                </>
              ) : isResetPassword ? (
                'Enviar Link de Recuperação'
              ) : isSignUp ? (
                'Criar Conta'
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            {!isResetPassword && (
              <button
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError('');
                  setSuccess('');
                }}
                className="text-brand-teal hover:text-brand-teal/80 text-sm font-medium"
              >
                {isSignUp ? 'Já tem uma conta? Entre' : 'Não tem uma conta? Cadastre-se'}
              </button>
            )}

            {!isSignUp && (
              <div>
                {isResetPassword ? (
                  <button
                    onClick={resetForm}
                    className="text-brand-teal hover:text-brand-teal/80 text-sm font-medium"
                  >
                    Voltar para o login
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setIsResetPassword(true);
                      setError('');
                      setSuccess('');
                    }}
                    className="text-brand-teal hover:text-brand-teal/80 text-sm font-medium"
                  >
                    Esqueceu sua senha?
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Terms of Use Modal */}
      {termsModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-brand-teal" />
                <h2 className="text-xl font-bold text-gray-900">Termos de Uso</h2>
              </div>
              <button
                onClick={() => setTermsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
            </div>
            
            <div className="prose max-w-none">
              <h3>1. Aceitação dos Termos</h3>
              <p>
                Ao acessar e usar o aplicativo SdT BSB, você concorda em cumprir e estar vinculado a estes Termos de Uso. Se você não concordar com qualquer parte destes termos, não poderá acessar o aplicativo.
              </p>
              
              <h3>2. Uso do Aplicativo</h3>
              <p>
                O aplicativo SdT BSB é fornecido para uso pessoal e não comercial. Você concorda em usar o aplicativo apenas para fins legais e de acordo com estes Termos de Uso.
              </p>
              
              <h3>3. Contas de Usuário</h3>
              <p>
                Para acessar determinados recursos do aplicativo, você precisará criar uma conta. Você é responsável por manter a confidencialidade de suas credenciais de conta e por todas as atividades que ocorrerem em sua conta.
              </p>
              
              <h3>4. Conteúdo do Usuário</h3>
              <p>
                Ao enviar conteúdo para o aplicativo (como pedidos de oração, testemunhos, etc.), você concede à Igreja Sal da Terra BSB uma licença mundial, não exclusiva, isenta de royalties para usar, reproduzir e distribuir esse conteúdo em relação ao aplicativo.
              </p>
              
              <h3>5. Conduta do Usuário</h3>
              <p>
                Você concorda em não usar o aplicativo para:
              </p>
              <ul>
                <li>Violar qualquer lei aplicável</li>
                <li>Publicar conteúdo difamatório, obsceno ou ofensivo</li>
                <li>Assediar, ameaçar ou intimidar outros usuários</li>
                <li>Tentar acessar áreas restritas do aplicativo</li>
                <li>Interferir no funcionamento do aplicativo</li>
              </ul>
              
              <h3>6. Propriedade Intelectual</h3>
              <p>
                O aplicativo e todo o seu conteúdo, recursos e funcionalidades são de propriedade da Igreja Sal da Terra BSB e estão protegidos por leis de direitos autorais, marcas registradas e outras leis de propriedade intelectual.
              </p>
              
              <h3>7. Limitação de Responsabilidade</h3>
              <p>
                O aplicativo é fornecido "como está" e "conforme disponível", sem garantias de qualquer tipo. A Igreja Sal da Terra BSB não será responsável por quaisquer danos diretos, indiretos, incidentais, consequenciais ou punitivos resultantes do uso ou incapacidade de usar o aplicativo.
              </p>
              
              <h3>8. Modificações dos Termos</h3>
              <p>
                A Igreja Sal da Terra BSB reserva-se o direito de modificar estes Termos de Uso a qualquer momento. As alterações entrarão em vigor imediatamente após a publicação dos termos atualizados no aplicativo.
              </p>
              
              <h3>9. Rescisão</h3>
              <p>
                A Igreja Sal da Terra BSB pode encerrar ou suspender seu acesso ao aplicativo a qualquer momento, por qualquer motivo, sem aviso prévio.
              </p>
              
              <h3>10. Lei Aplicável</h3>
              <p>
                Estes Termos de Uso são regidos pelas leis do Brasil. Qualquer disputa relacionada a estes termos será submetida à jurisdição exclusiva dos tribunais de Brasília, DF.
              </p>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setTermsModalOpen(false);
                  setAgreedToTerms(true);
                }}
                className="bg-brand-teal text-white px-6 py-2 rounded-xl hover:bg-brand-teal/90 transition-colors flex items-center gap-2"
              >
                <CheckSquare className="h-4 w-4" />
                Concordo com os Termos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {privacyModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-brand-teal" />
                <h2 className="text-xl font-bold text-gray-900">Política de Privacidade</h2>
              </div>
              <button
                onClick={() => setPrivacyModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
            </div>
            
            <div className="prose max-w-none">
              <p>
                A Igreja Sal da Terra BSB está comprometida em proteger sua privacidade. Esta Política de Privacidade explica como coletamos, usamos e protegemos suas informações pessoais.
              </p>
              
              <h3>1. Informações que Coletamos</h3>
              <p>
                Podemos coletar os seguintes tipos de informações:
              </p>
              <ul>
                <li>Informações de identificação pessoal (nome, email, telefone)</li>
                <li>Informações de login e autenticação</li>
                <li>Conteúdo gerado pelo usuário (pedidos de oração, testemunhos, etc.)</li>
                <li>Informações de uso e interação com o aplicativo</li>
                <li>Informações do dispositivo e conexão</li>
              </ul>
              
              <h3>2. Como Usamos Suas Informações</h3>
              <p>
                Usamos suas informações para:
              </p>
              <ul>
                <li>Fornecer e manter o aplicativo</li>
                <li>Personalizar sua experiência</li>
                <li>Processar pedidos de oração e solicitações de orientação espiritual</li>
                <li>Enviar notificações importantes sobre o aplicativo ou eventos da igreja</li>
                <li>Melhorar nosso aplicativo e serviços</li>
                <li>Cumprir obrigações legais</li>
              </ul>
              
              <h3>3. Compartilhamento de Informações</h3>
              <p>
                Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros para fins comerciais. Podemos compartilhar suas informações:
              </p>
              <ul>
                <li>Com líderes da igreja para responder a pedidos de oração ou orientação espiritual</li>
                <li>Com provedores de serviços que nos ajudam a operar o aplicativo</li>
                <li>Quando exigido por lei ou para proteger nossos direitos</li>
              </ul>
              
              <h3>4. Segurança de Dados</h3>
              <p>
                Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações pessoais contra acesso não autorizado, perda ou alteração.
              </p>
              
              <h3>5. Seus Direitos</h3>
              <p>
                Você tem o direito de:
              </p>
              <ul>
                <li>Acessar suas informações pessoais</li>
                <li>Corrigir informações imprecisas</li>
                <li>Excluir suas informações pessoais</li>
                <li>Retirar seu consentimento a qualquer momento</li>
                <li>Opor-se ao processamento de suas informações</li>
              </ul>
              
              <h3>6. Cookies e Tecnologias Semelhantes</h3>
              <p>
                Usamos cookies e tecnologias semelhantes para melhorar sua experiência, entender como o aplicativo é usado e personalizar o conteúdo.
              </p>
              
              <h3>7. Alterações a Esta Política</h3>
              <p>
                Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre quaisquer alterações significativas publicando a nova Política de Privacidade no aplicativo.
              </p>
              
              <h3>8. Contato</h3>
              <p>
                Se você tiver dúvidas sobre esta Política de Privacidade, entre em contato conosco pelo email: privacidade@sdtbsb.com.br
              </p>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setPrivacyModalOpen(false);
                  setAgreedToTerms(true);
                }}
                className="bg-brand-teal text-white px-6 py-2 rounded-xl hover:bg-brand-teal/90 transition-colors flex items-center gap-2"
              >
                <CheckSquare className="h-4 w-4" />
                Concordo com a Política
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}